/**
 * Availability API
 *
 * Functions for interacting with the availability endpoints.
 */

import { api } from './client';

// =============================================================================
// Types
// =============================================================================

export type AvailabilityStatus = 'AVAILABLE' | 'NOT_AVAILABLE' | 'MAYBE';

export interface Availability {
  id: string;
  userId: string;
  eventId: string;
  /** Null = no response yet (a note can exist before a status is chosen). */
  status: AvailabilityStatus | null;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string | null;
  };
  event?: {
    id: string;
    name: string;
    venue: string;
    eventDate: string;
    callTime?: string | null;
    startTime?: string | null;
    endTime?: string | null;
    status: string;
    sport?: string | null;
    eventType?: string | null;
  };
}

export interface AvailabilitySummary {
  available: number;
  maybe: number;
  notAvailable: number;
  total: number;
}

export interface PendingEvent {
  id: string;
  name: string;
  venue: string;
  eventDate: string;
  callTime?: string | null;
  startTime?: string | null;
  sport?: string | null;
  eventType?: string | null;
}

// =============================================================================
// Request/Response Types
// =============================================================================

export interface SetAvailabilityInput {
  /** Omit to save a note without touching the status (note-only save). */
  status?: AvailabilityStatus;
  notes?: string | null;
}

export interface UpdateAvailabilityInput {
  status?: AvailabilityStatus;
  notes?: string | null;
}

export interface ListAvailabilityParams {
  eventId?: string;
  userId?: string;
  status?: AvailabilityStatus;
  page?: number;
  limit?: number;
}

export interface ListAvailabilityResponse {
  availability: Availability[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface EventAvailabilityResponse {
  availability: Availability[];
  summary: AvailabilitySummary;
}

/** One active roster member's answer for an event (admin "See Availability" view). */
export interface EventAvailabilityRosterEntry {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  orgRole: 'OWNER' | 'ADMIN' | 'CREW';
  /** Null = never answered. A row with a null status is a note left before answering. */
  availability: {
    status: AvailabilityStatus | null;
    notes?: string | null;
    updatedAt: string;
  } | null;
  /** Live (ASSIGNED / CONFIRMED) seats on this event; declined seats are not listed. */
  assignments: { roleName: string; status: 'ASSIGNED' | 'CONFIRMED' }[];
}

export interface EventAvailabilityRosterResponse {
  crew: EventAvailabilityRosterEntry[];
  summary: AvailabilitySummary & { noResponse: number };
}

// =============================================================================
// Availability CRUD
// =============================================================================

/**
 * List availability records with optional filters
 */
export async function listAvailability(
  params?: ListAvailabilityParams
): Promise<ListAvailabilityResponse> {
  const response = await api.get<ListAvailabilityResponse>('/availability', { params });
  return response.data;
}

/**
 * Get a single availability record by ID
 */
export async function getAvailability(id: string): Promise<{ availability: Availability }> {
  const response = await api.get<{ availability: Availability }>(`/availability/${id}`);
  return response.data;
}

/**
 * Get current user's availability for all events
 */
export async function getMyAvailability(): Promise<{ availability: Availability[] }> {
  const response = await api.get<{ availability: Availability[] }>('/availability/my');
  return response.data;
}

/**
 * Get events the user hasn't responded to yet
 */
export async function getPendingEvents(): Promise<{ events: PendingEvent[] }> {
  const response = await api.get<{ events: PendingEvent[] }>('/availability/pending-events');
  return response.data;
}

/**
 * Update an availability record
 */
export async function updateAvailability(
  id: string,
  data: UpdateAvailabilityInput
): Promise<{ message: string; availability: Availability }> {
  const response = await api.put<{ message: string; availability: Availability }>(
    `/availability/${id}`,
    data
  );
  return response.data;
}

/**
 * Delete an availability record
 */
export async function deleteAvailability(id: string): Promise<{ message: string }> {
  const response = await api.delete<{ message: string }>(`/availability/${id}`);
  return response.data;
}

// =============================================================================
// Event-Scoped Availability
// =============================================================================

/**
 * Get all availability for an event
 */
export async function getEventAvailability(
  eventId: string
): Promise<EventAvailabilityResponse> {
  const response = await api.get<EventAvailabilityResponse>(
    `/events/${eventId}/availability`
  );
  return response.data;
}

/**
 * Every active roster member with their answer for an event (admin only).
 * Unlike getEventAvailability this includes people who never answered.
 */
export async function getEventAvailabilityRoster(
  eventId: string
): Promise<EventAvailabilityRosterResponse> {
  const response = await api.get<EventAvailabilityRosterResponse>(
    `/events/${eventId}/availability-roster`
  );
  return response.data;
}

/**
 * Set availability for an event (creates or updates)
 */
export async function setAvailability(
  eventId: string,
  data: SetAvailabilityInput
): Promise<{ message: string; availability: Availability }> {
  const response = await api.post<{ message: string; availability: Availability }>(
    `/events/${eventId}/availability`,
    data
  );
  return response.data;
}
